import { gql } from "_graphql-types-frontend/gql";
import { gql as rawGql } from "@apollo/client";
import { ChatEffort, LlmModelRole } from "_graphql-types-frontend/graphql";

export const AGENT_CHAT = gql(/* GraphQL */ `
  query AgentChat(
    $question: String!
    $conversationId: Int
    $chatContext: ChatContext
    $tools: [ChatToolType!]
    $sessionId: String!
    $model: String
    $effort: ChatEffort
    $excludedMcpCategories: [SiriusMcpCategory!]
  ) {
    # Returns once the turn has started; the answer arrives on the subscription.
    chat(
      question: $question
      conversationId: $conversationId
      chatContext: $chatContext
      tools: $tools
      sessionId: $sessionId
      model: $model
      effort: $effort
      excludedMcpCategories: $excludedMcpCategories
    ) {
      conversationId
    }
  }
`);

export const CONTEXTUAL_CHAT_SUBSCRIPTION = gql(/* GraphQL */ `
  subscription RangerChatSubscription($sessionId: String!) {
    rangerChatSubscription(sessionId: $sessionId) {
      answer
      conversationId
      model
      effort
      tools {
        toolId
        toolCallId
        tool
        toolName
        params
        results
      }
      sessionId
      messageStream {
        message
        messageIndex
        contentType
        blockIndex
        event
        id
      }
    }
  }
`);

export const STOP_CHAT = gql(/* GraphQL */ `
  mutation StopChat($sessionId: String!) {
    stopChat(sessionId: $sessionId)
  }
`);

export const GET_KNOWLEDGE_BASE = gql(/* GraphQL */ `
  query GetSavedKnowledgeBase($id: Int!) {
    savedKnowledgeBase(id: $id) {
      id
      name
      status
      filters
      noteCount
      documentCount
      investmentCount
      firmCount
      companyCount
      updatedAt
    }
  }
`);

export const MY_CONVERSATIONS = gql(/* GraphQL */ `
  query MyConversations($limit: Int, $offset: Int, $sources: [ConversationSource!]) {
    myConversations(limit: $limit, offset: $offset, sources: $sources) {
      items {
        id
        summary
        createDate
        lastActivityDate
        hasMutationPlans
      }
      total
    }
  }
`);

export const LOAD_CONVERSATION = gql(/* GraphQL */ `
  query LoadConversation($conversationId: Int!) {
    loadConversation(conversationId: $conversationId) {
      id
      question
      answer
      createDate
      chatContext
    }
  }
`);

export const MUTATION_PLANS_BY_CONVERSATION = gql(/* GraphQL */ `
  query MutationPlansByConversation($conversationId: Int!) {
    mutationPlansByConversation(conversationId: $conversationId) {
      id
      title
      status
    }
  }
`);

export const CONVERSATION_ARTIFACTS = gql(/* GraphQL */ `
  query ConversationArtifacts($conversationId: Int!) {
    conversationArtifacts(conversationId: $conversationId) {
      artifactName
      downloadUrl
    }
  }
`);

export const CHAT_REPORT_FILES = gql(/* GraphQL */ `
  query ChatReportFiles($conversationId: Int!) {
    conversationArtifacts(conversationId: $conversationId) {
      id
      downloadUrl
      metadata
    }
  }
`);

export const CHAT_TASK_FINISHED = gql(/* GraphQL */ `
  subscription ChatTaskFinished($taskId: String!) {
    chatTaskFinished(taskId: $taskId) {
      taskId
    }
  }
`);

export const DELETE_STORED_PROMPT = gql(/* GraphQL */ `
  mutation DeleteStoredPrompt($id: ID!) {
    deleteStoredPrompts(id: $id)
  }
`);

export const DELETE_CONVERSATION = gql(/* GraphQL */ `
  mutation DeleteConversation($conversationId: Int!) {
    deleteConversation(conversationId: $conversationId)
  }
`);

export const GET_CURRENT_USER_SETTINGS = gql(/* GraphQL */ `
  query CurrentUserChatPreferences {
    currentUserSettings {
      id
      userId
      data {
        chatPreferences {
          enabled
          preferences
          lastUpdated
        }
      }
    }
  }
`);

export const UPDATE_USER_SETTINGS = gql(/* GraphQL */ `
  mutation UpdateChatPreferences($input: UserSettingsInput!) {
    addOrUpdateUserSettings(input: $input) {
      id
    }
  }
`);

export const MY_ACTIVE_CHAT_SKILLS = gql(/* GraphQL */ `
  query MyActiveChatSkills {
    myActiveChatSkills {
      id
      name
      description
      instructions
      isAutoInvocable
      scope
      organizationId
      createUser
      createUserFullName
      canEdit
      canDelete
    }
  }
`);

export const AVAILABLE_ORG_CHAT_SKILLS = gql(/* GraphQL */ `
  query AvailableOrgChatSkills($organizationId: Int!) {
    availableOrgChatSkills(organizationId: $organizationId) {
      id
      name
      description
      instructions
      isAutoInvocable
      scope
      organizationId
      createUser
      createUserFullName
      canEdit
      canDelete
    }
  }
`);

export const CREATE_CHAT_SKILL = gql(/* GraphQL */ `
  mutation CreateChatSkill($input: ChatSkillInput!) {
    createChatSkill(input: $input) {
      id
      name
      description
      instructions
      isAutoInvocable
      scope
      organizationId
      createUser
      createUserFullName
    }
  }
`);

export const UPDATE_CHAT_SKILL = gql(/* GraphQL */ `
  mutation UpdateChatSkill($id: Int!, $input: ChatSkillUpdateInput!) {
    updateChatSkill(id: $id, input: $input) {
      id
      name
      description
      instructions
      isAutoInvocable
      scope
    }
  }
`);

export const DELETE_CHAT_SKILL = gql(/* GraphQL */ `
  mutation DeleteChatSkill($id: Int!) {
    deleteChatSkill(id: $id)
  }
`);

export const OPT_IN_TO_CHAT_SKILL = gql(/* GraphQL */ `
  mutation OptInToChatSkill($chatSkillId: Int!) {
    optInToChatSkill(chatSkillId: $chatSkillId) {
      id
      chatSkillId
    }
  }
`);

export const OPT_OUT_OF_CHAT_SKILL = gql(/* GraphQL */ `
  mutation OptOutOfChatSkill($chatSkillId: Int!) {
    optOutOfChatSkill(chatSkillId: $chatSkillId)
  }
`);

export const SYSTEM_CHAT_SKILLS = rawGql(`
  query SystemChatSkills {
    chatSkillsByScope(scope: SYSTEM) {
      id
      name
      description
      isAutoInvocable
    }
  }
`);

// Group write-access grants on a skill, plus the picker that feeds them.
// `canEdit` on a skill already tells the UI whether the current user may write
// it — these are only for the owner's "who else can edit this" panel.
export const CHAT_SKILL_GROUP_ACCESS = rawGql(`
  query ChatSkillGroupAccess($chatSkillId: Int!) {
    chatSkillGroupAccess(chatSkillId: $chatSkillId) {
      id
      chatSkillId
      groupId
      access
      groupName
    }
  }
`);

export const GRANT_CHAT_SKILL_GROUP_ACCESS = rawGql(`
  mutation GrantChatSkillGroupAccess($chatSkillId: Int!, $groupId: Int!) {
    grantChatSkillGroupAccess(chatSkillId: $chatSkillId, groupId: $groupId) {
      id
      groupId
    }
  }
`);

export const REVOKE_CHAT_SKILL_GROUP_ACCESS = rawGql(`
  mutation RevokeChatSkillGroupAccess($chatSkillId: Int!, $groupId: Int!) {
    revokeChatSkillGroupAccess(chatSkillId: $chatSkillId, groupId: $groupId)
  }
`);

// Groups in the skill's organization, for the access picker. The server
// re-validates the chosen id on grant — this only makes the choice convenient.
// Hits its own coreUser-gated query rather than `userList` (isSubscriptionUser
// gated) so a skill's owner without that flag can still browse groups, and that
// query returns a two-scalar type rather than `User` rows, so the picker cannot
// be used to expand a group into its member roster.
export const ORGANIZATION_GROUPS = rawGql(`
  query ChatSkillGrantableGroups($organizationId: Int!, $q: String) {
    chatSkillGrantableGroups(organizationId: $organizationId, q: $q) {
      id
      name
    }
  }
`);

export const CHAT_SKILL_HISTORY = rawGql(`
  query ChatSkillHistory($id: Int!) {
    chatSkillHistory(id: $id) {
      id
      version
      name
      description
      instructions
      archivedAt
      archivedBy
      archivedByFullName
    }
  }
`);

export const MY_CHAT_SKILL_SCHEDULES = gql(/* GraphQL */ `
  query MyChatSkillSchedules {
    chatSkillScheduleList(sort: [{ field: createdate, order: DESC }]) {
      items {
        id
        chatSkillId
        frequency
        runAtHour
        runAtMinute
        runOnWeekday
        runOnDayOfMonth
        timezone
        extraInstructions
        enabled
        nextRunAt
        lastRunAt
        lastRunStatus
        lastConversationId
      }
    }
  }
`);

export const ADD_CHAT_SKILL_SCHEDULE = gql(/* GraphQL */ `
  mutation AddChatSkillSchedule($input: ChatSkillScheduleInput!) {
    addChatSkillSchedule(input: $input) {
      id
    }
  }
`);

export const UPDATE_CHAT_SKILL_SCHEDULE = gql(/* GraphQL */ `
  mutation UpdateChatSkillSchedule($id: Int!, $input: ChatSkillScheduleInput!) {
    updateChatSkillSchedule(id: $id, input: $input) {
      id
    }
  }
`);

export const DELETE_CHAT_SKILL_SCHEDULE = gql(/* GraphQL */ `
  mutation DeleteChatSkillSchedule($id: Int!) {
    deleteChatSkillSchedule(id: $id)
  }
`);

export const RUN_CHAT_SKILL_SCHEDULE_NOW = gql(/* GraphQL */ `
  mutation RunChatSkillScheduleNow($id: Int!) {
    runChatSkillScheduleNow(id: $id)
  }
`);

/**
 * Chat agents (issue 5224).
 *
 * `rawGql`, not the codegen-typed `gql`, matching how the 4972 additions were
 * introduced: the generated types are not committed, so a typed document here
 * would not compile until codegen runs.
 */
export const MY_SPAWNABLE_CHAT_AGENTS = rawGql(`
  query MySpawnableChatAgents {
    mySpawnableChatAgents {
      id
      name
      description
      directive
      tools
      model
      effort
      color
      defaultMode
      scope
      organizationId
      version
      createUser
      createUserFullName
      canEdit
      canDelete
      skills {
        id
        name
      }
    }
  }
`);

export const CHAT_AGENTS_BY_SCOPE = rawGql(`
  query ChatAgentsByScope($scope: ChatSkillScope!, $organizationId: Int) {
    chatAgentsByScope(scope: $scope, organizationId: $organizationId) {
      id
      name
      description
      directive
      tools
      model
      effort
      color
      defaultMode
      scope
      organizationId
      version
      createUser
      createUserFullName
      canEdit
      canDelete
      skills {
        id
        name
      }
    }
  }
`);

export const CREATE_CHAT_AGENT = rawGql(`
  mutation CreateChatAgent($input: ChatAgentInput!) {
    createChatAgent(input: $input) {
      id
      name
      description
      directive
      tools
      model
      effort
      color
      defaultMode
      scope
      organizationId
      version
      createUser
      createUserFullName
      canEdit
      canDelete
      skills {
        id
        name
      }
    }
  }
`);

export const UPDATE_CHAT_AGENT = rawGql(`
  mutation UpdateChatAgent($id: Int!, $input: ChatAgentUpdateInput!) {
    updateChatAgent(id: $id, input: $input) {
      id
      name
      description
      directive
      tools
      model
      effort
      color
      defaultMode
      scope
      organizationId
      version
      createUser
      createUserFullName
      canEdit
      canDelete
      skills {
        id
        name
      }
    }
  }
`);

export const DELETE_CHAT_AGENT = rawGql(`
  mutation DeleteChatAgent($id: Int!) {
    deleteChatAgent(id: $id)
  }
`);

export const CHAT_AGENT_HISTORY = rawGql(`
  query ChatAgentHistory($id: Int!) {
    chatAgentHistory(id: $id) {
      id
      version
      name
      description
      directive
      tools
      model
      effort
      defaultMode
      scope
      skills
      archivedAt
      archivedBy
    }
  }
`);

/**
 * What an agent may be configured with. Served rather than mirrored — a client
 * copy of these policies has drifted every time it has existed.
 */
export const CHAT_AGENT_CAPABILITIES = rawGql(`
  query ChatAgentCapabilities {
    chatAgentCapabilities {
      allowedModels
      allowedEfforts
      allowedColors
      blockedTools
      mcpToolBundles {
        key
        title
        description
        tools {
          name
          description
        }
      }
      mcpToolCategories {
        key
        title
        tools {
          name
        }
      }
      notOfferedToAgents {
        key
        title
        description
        tools {
          name
        }
      }
    }
  }
`);

/**
 * The registry rows; the picker and the request both read this. Super users
 * also get inactive candidates, and an active row may serve only sub-agents,
 * so every chat reader filters through `servesChat`.
 */
export const LLM_MODELS = gql(/* GraphQL */ `
  query LlmModels {
    llmModels {
      key
      displayName
      description
      supportsEffort
      defaultEffort
      isDefault
      enabled
      verifiedRoles
      intelligenceIndex
      outputTokensPerSecond
      blendedPricePer1M
      effortBenchmarks {
        effort
        intelligenceIndex
        outputTokensPerSecond
      }
      behavior {
        feature
        limited
      }
    }
  }
`);

/** Required wherever these benchmarks are shown (Artificial Analysis free-API terms). */
export const BENCHMARK_SOURCE_URL = "https://artificialanalysis.ai/";

const EFFORT_LABELS: Record<string, string> = { low: "Low", medium: "Medium", high: "High", xhigh: "Extra high" };

/**
 * How a reasoning effort reads anywhere in chat: the picker, the agent form
 * and the line under an answer. Takes the GraphQL enum ("XHigh") or the
 * value the server returns ("xhigh").
 */
export const effortLabel = (effort: string) => EFFORT_LABELS[effort.toLowerCase()] ?? effort;

/** A stored effort ("high") as the picker's enum (High), or null for none or one it does not know. */
export const chatEffortOf = (effort: string | null | undefined): ChatEffort | null =>
  Object.values(ChatEffort).find(e => e.toLowerCase() === effort?.toLowerCase()) ?? null;

type EffortRun = { effort: string; intelligenceIndex?: number | null; outputTokensPerSecond?: number | null };

type StatsModel = {
  key?: string;
  isDefault?: boolean;
  intelligenceIndex?: number | null;
  outputTokensPerSecond?: number | null;
  blendedPricePer1M?: number | null;
  /** Artificial Analysis's run at each reasoning effort, lowest first (null: one run only). */
  effortBenchmarks?: readonly EffortRun[] | null;
};

/** The reasoning effort a model will run at, by model: the user's pick, else its default; null for its own default. */
export type EffortOf = (m: StatsModel) => string | null | undefined;

/**
 * The model's benchmarks at `effort`, when Artificial Analysis ran it there.
 * Unknown (null) when it ran other efforts but not this one: its headline run
 * is often Max, and showing that for a lower effort is the overstatement
 * per-effort runs exist to avoid. The headline run stands only when there is
 * no effort to match, or no per-effort runs at all.
 */
export function benchmarksAt(m: StatsModel, effort?: string | null): { intelligenceIndex: number | null; outputTokensPerSecond: number | null } {
  if (effort && m.effortBenchmarks?.length) {
    // The picker's enum is "High"; Artificial Analysis's runs are stored as "high".
    const run = m.effortBenchmarks.find(r => r.effort === effort.toLowerCase());
    return { intelligenceIndex: run?.intelligenceIndex ?? null, outputTokensPerSecond: run?.outputTokensPerSecond ?? null };
  }
  return { intelligenceIndex: m.intelligenceIndex ?? null, outputTokensPerSecond: m.outputTokensPerSecond ?? null };
}

/** Lowest and highest intelligence across the efforts Artificial Analysis ran, or null without two. */
export function intelligenceRange(m: StatsModel): [number, number] | null {
  const scores = (m.effortBenchmarks ?? []).map(r => r.intelligenceIndex).filter((v): v is number => v != null);
  return scores.length >= 2 ? [Math.min(...scores), Math.max(...scores)] : null;
}

/** Intelligence per unit of cost at `effort`: a higher number buys more capability for the same spend. */
const pointsPerCost = (m: StatsModel, effort?: string | null) => {
  const { intelligenceIndex } = benchmarksAt(m, effort);
  return m.blendedPricePer1M && intelligenceIndex ? intelligenceIndex / m.blendedPricePer1M : null;
};

/** What "Value" means, for a tooltip wherever it is shown. Worded without money, as the rating itself is. */
export const VALUE_EXPLAINED =
  "How much capability a model gives for what it takes to run, compared with the other models offered here: the best scores 10. Capability is the Artificial Analysis Intelligence Index at the reasoning effort the model will run at.";

/**
 * Value out of 10 against `peers`, the models offered alongside it, each at
 * the effort it would run at (`effortOf`): the one with the most intelligence
 * per unit of cost scores 10, the rest in proportion (never below 1). Shows
 * value without showing money. Null when fewer than two offered models can be
 * compared.
 */
export function valueRating(
  m: StatsModel,
  peers: readonly StatsModel[],
  effortOf: EffortOf = () => null
): number | null {
  const own = pointsPerCost(m, effortOf(m));
  const rated = peers.map(p => pointsPerCost(p, effortOf(p))).filter((v): v is number => v != null);
  if (own == null || rated.length < 2) return null;
  return Math.max(1, Math.round((10 * own) / Math.max(...rated)));
}

/**
 * One line a user can compare models by: intelligence and speed at the effort
 * the model will run at, the intelligence range across efforts, and value
 * against `peers`. Missing numbers are left out rather than shown as zero.
 */
export function modelStats(
  m: StatsModel,
  peers: readonly StatsModel[] = [],
  effortOf: EffortOf = () => null
): string {
  const parts: string[] = [];
  const { intelligenceIndex, outputTokensPerSecond } = benchmarksAt(m, effortOf(m));
  const range = intelligenceRange(m);
  const spread = range && range[0] !== range[1] ? `${Math.round(range[0])}–${Math.round(range[1])} by effort` : null;
  if (intelligenceIndex != null) parts.push(`Intelligence ${Math.round(intelligenceIndex)}${spread ? ` (${spread})` : ""}`);
  // Not run at this effort: the range is still known, a single figure is not.
  else if (spread) parts.push(`Intelligence ${spread}`);
  if (outputTokensPerSecond != null) parts.push(`${Math.round(outputTokensPerSecond)} tokens/s`);
  const value = valueRating(m, peers, effortOf);
  if (value != null) parts.push(`Value ${value}/10`);
  return parts.join(" · ");
}

/** Features this model gets less of in chat, e.g. "Visual PDF reading". */
export const limitedFeatures = (m: {
  behavior?: { feature: string; limited: boolean }[] | null;
}) => (m.behavior ?? []).filter(b => b.limited).map(b => b.feature);

/** Pickable for chat: active, and its compatibility eval passed the chat role. */
export const servesChat = (m: {
  enabled: boolean;
  verifiedRoles: readonly string[];
}) =>
  // GraphQL enum name ("Chat"), not the server's stored value ("chat").
  m.enabled && m.verifiedRoles.includes(LlmModelRole.Chat);
